import { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { useFocusEffect, useRoute } from '@react-navigation/native';
import moment from 'moment';
import { useSelector } from 'react-redux';

import { NewLink } from '@components/operatorPanel/components/OperatorPanelInformation/components/OperatorPanelInformationNotesScreen/EditScreen';

import { useBuildImageContextForAdding } from '@components/operatorPanel/hooks/useBuildImageContextForAdding';
import { DOC_TYPE, KEYS_TAGS } from '@components/imageHandling/common';
import { scheduledProductionOperationIdParam } from '@components/operatorPanel/constants/operatorPanel.constants';
import {
  addOrUpdateImageDocument,
  selectCurrentContextImagesDocuments,
  selectCurrentContextLoading,
  selectCurrentImageDocumentContext,
  selectIsNoteAdding,
  selectSelectedNote,
  setIsNoteAdding,
  setSelectedNoteId,
} from '../../../../../../../reducers/v2/imageHandlingV2/imageHandlingV2.slice';
import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { selectUserInfo } from '@reducers/user';
import { updateImageMetadata, uploadImageDocument } from '@reducers/v2/imageHandlingV2/thunks';
import { updateExistingFileInBlobStorage } from '@reducers/v2/imageHandlingV2/thunks/updateExistingFileInBlobStorage';

export const NOTES_SCREEN_STATES = {
  LOADING: 'loading',
  EMPTY: 'empty',
  CREATE: 'create',
  EDIT: 'edit',
  PREVIEW: 'preview',
};

type NoteScreenState = (typeof NOTES_SCREEN_STATES)[keyof typeof NOTES_SCREEN_STATES];

export const useManageOPNotes = () => {
  const route = useRoute();

  const dispatch = useAppDispatch();
  const imageContext = useSelector(selectCurrentImageDocumentContext);
  const isNoteAdding = useSelector(selectIsNoteAdding);
  const userInfo = useSelector(selectUserInfo);
  const imagesDocumentsLoading = useSelector(selectCurrentContextLoading);

  const imagesDocuments = useSelector(selectCurrentContextImagesDocuments);

  const notesData = imagesDocuments.filter((document) => document.documentTag === DOC_TYPE.NOTE);
  const notes = notesData
    .map((img) => ({
      ...img,
      title: img.title || img.fileName || `Note ${img.id}`,
      fileName: img.fileName || `note-${img.id}.rtf`,
      fileNameOverride: img.fileNameOverride ?? img.fileName,
      extensionOriginal: img.fileName?.split('.').pop() || 'rtf',
      links: img.links || [],
    }))
    .sort((a, b) => {
      // Sort by ID descending (newest/highest ID first)
      const aId = a.documentId || a.id || 0;
      const bId = b.documentId || b.id || 0;
      return bId - aId;
    });

  const activeNote = useSelector(selectSelectedNote);

  const [screenState, setScreenState] = useState<NoteScreenState>(NOTES_SCREEN_STATES.LOADING);
  const [touched, setTouched] = useState<boolean>(false);
  const [editorValue, setEditorValue] = useState('');

  const [isRenaming, setIsRenaming] = useState<boolean>(false);
  const [editTitle, setEditTitle] = useState(
    (activeNote?.fileNameOverride ?? activeNote?.title ?? activeNote?.fileName)?.replace?.('.rtf', '') ?? ''
  );
  const [newNoteLinks, setNewNoteLinks] = useState<NewLink[]>([]);

  const selectedJobId = route.params?.[scheduledProductionOperationIdParam];
  const defaultNoteLinks = useBuildImageContextForAdding(
    selectedJobId,
    [KEYS_TAGS.ProductionOperation].concat(selectedJobId ? [] : [KEYS_TAGS.ProductionBatch])
  );

  const cancelAlert = useRef<() => void>();

  // Memoize notes to prevent flickering when data hasn't actually changed
  const memoizedNotes = useMemo(() => notes, [JSON.stringify(notes)]);

  const noteWasAdding = useRef<boolean>(false);

  useFocusEffect(
    useCallback(() => {
      if (isNoteAdding) {
        noteWasAdding.current = true;
      } else {
        noteWasAdding.current = false;
        setScreenState(NOTES_SCREEN_STATES.PREVIEW);
        dispatch(setSelectedNoteId(null));
        setTouched(false);
        setIsRenaming(false);
        setEditTitle('');
        setNewNoteLinks([]);
      }
      // if (!isNoteAdding && noteWasAdding.current) {
      // }
    }, [isNoteAdding])
  );

  useFocusEffect(
    useCallback(() => {
      if (screenState === NOTES_SCREEN_STATES.CREATE) {
        setNewNoteLinks(defaultNoteLinks);
      }
    }, [screenState])
  );

  const onSave = async () => {
    if (screenState === NOTES_SCREEN_STATES.EDIT && !(touched || isRenaming)) {
      setScreenState(NOTES_SCREEN_STATES.PREVIEW);
      dispatch(setSelectedNoteId(null));
      return;
    }

    const fileName = `Note ${moment().format('YYYY-MM-DD [at] HH:mm:ss')} by ${userInfo.userProfile?.firstName} ${
      userInfo.userProfile?.lastName
    }.rtf`;

    const file = new File([editorValue], fileName, { type: 'application/rtf' });

    if (activeNote) {
      if (isRenaming) {
        if (activeNote.title === editTitle) {
          setIsRenaming(false);
          return;
        }

        const newFileName = editTitle.endsWith('.rtf') ? editTitle : `${editTitle}.rtf`;
        const backendData = {
          documentId: activeNote.documentId ?? activeNote?.id,
          newFileName,
        };

        await dispatch(updateImageMetadata({ data: backendData }));

        dispatch(addOrUpdateImageDocument({ document: { ...activeNote, title: newFileName, fileNameOverride: newFileName }, imageContext }));
        dispatch(setSelectedNoteId(activeNote.documentId));

        setIsRenaming(false);

        return;
      } else if (touched) {
        await dispatch(
          updateExistingFileInBlobStorage({ contentHash: activeNote.contentHash, fileName: activeNote.fileName, file, textContent: editorValue })
        );
      }
    } else {
      // Fixed: Use only the user-selected tags from newNoteLinks
      const defaultLinks = newNoteLinks.map((link) => ({ ...link, isFavorite: true }));

      const tempID = `TEMPIMAGEID-${fileName}`;

      dispatch(setIsNoteAdding(true));

      await dispatch(
        uploadImageDocument({
          imageContexts: [imageContext],
          documentType: DOC_TYPE.NOTE,
          photo: file,
          tags: defaultLinks,
          rawContent: editorValue,
          name: fileName,
          tempID,
        })
      );

      setTimeout(() => {
        dispatch(setIsNoteAdding(false));
      }, 1000);
    }

    if (activeNote) {
      setScreenState(NOTES_SCREEN_STATES.PREVIEW);
      dispatch(setSelectedNoteId(null));
      setTouched(false);
      setIsRenaming(false);
      setEditTitle('');
      setNewNoteLinks([]);
    }
  };

  useEffect(() => {
    // If we're still waiting for data, ensure we stay in loading state
    if (imagesDocumentsLoading) {
      if (screenState !== NOTES_SCREEN_STATES.LOADING) {
        setScreenState(NOTES_SCREEN_STATES.LOADING);
      }
      return;
    }

    // Once data is loaded, transition to appropriate state
    if (memoizedNotes.length > 0 && [NOTES_SCREEN_STATES.LOADING, NOTES_SCREEN_STATES.EMPTY].includes(screenState)) {
      setScreenState(NOTES_SCREEN_STATES.PREVIEW);
    } else if (memoizedNotes.length === 0 && [NOTES_SCREEN_STATES.LOADING, NOTES_SCREEN_STATES.PREVIEW].includes(screenState)) {
      setScreenState(NOTES_SCREEN_STATES.EMPTY);
    }
  }, [memoizedNotes, imagesDocumentsLoading, screenState]);

  useEffect(() => {
    if (!activeNote) {
      setScreenState(notes.length > 0 ? NOTES_SCREEN_STATES.PREVIEW : NOTES_SCREEN_STATES.EMPTY);
    } else {
      setScreenState(NOTES_SCREEN_STATES.EDIT);
      setEditTitle((activeNote?.fileNameOverride ?? activeNote?.title ?? activeNote?.fileName)?.replace?.('.rtf', ''));
    }
  }, [activeNote]);

  return {
    screenState,
    setScreenState,
    setEditorValue,
    setTouched,
    newNoteLinks,
    setNewNoteLinks,
    memoizedNotes,
    isRenaming,
    activeNote,
    setIsRenaming,
    touched,
    cancelAlert,
    editorValue,
    onSave,
    editTitle,
    setEditTitle,
    notes,
    isNoteAdding,
  };
};
